import { NoiseCancellationType, VirtualBackgroundType } from '@yandex-360/telemost-sdk/device-manager';
import { Event } from '@yandex-int/messenger.utils';
import {
    ConnectionConfig,
    ConnectionStatus,
    ConnectionType,
    DisplayConstraints,
    ElectronDisplayConstraints,
    NetworkQualityScore,
    Participant,
    Session,
    SessionEvent,
    SoundProcessorOptions,
    VideoProcessorOptions,
} from '@yandex-video-platform/goloom-sdk';
import { call, select } from 'typed-redux-saga';

import { NetworkQualityChangedEvent } from '../../../pages/call/features/network-quality/sagas';
import {
    NoiseCancellationTypeChangedEvent,
    VirtualBackgroundTypeChangedEvent,
} from '../../../pages/call/features/noise-cancellation/sagas';
import { ConnectionStatusEvent, NotificationEvent } from '../../../pages/call/types';
import { bind } from '../../helpers/decorators';
import { getCauseObject } from '../../helpers/get-cause-object';
import { createLogger } from '../../helpers/loggerFacade';
import { DEFAULT_FLAGS, DEFAULT_ICE_SERVERS, DEFAULT_MEDIA_SERVER_URL, DEFAULT_SERVICE_NAME } from './constants';
import { createDeviceFromTrack } from './helpers';
import { selectInitSessionConfig } from './selectors';
import { GoloomSdkError } from './types';

const logger = createLogger('goloom:join-diagnostics');

export class GoloomSDK {
    public roomId: Maybe<string>;
    public session: Maybe<Session>;
    private name: Maybe<string>;
    private localVad = false;
    private debugMode = false;
    private connectionStatus: Maybe<ConnectionStatus>;
    private networkScore = NetworkQualityScore.NETWORK_QUALITY_SCORE_UNSPECIFIED;

    public myUserId: Maybe<string>;
    public onInit = new Event<true>();
    public onJoin = new Event();
    public onDestroy = new Event<true>();
    public onParticipantsUpdated = new Event<Participant[]>();
    public onParticipantsAdded = new Event<Participant[]>();
    public onParticipantsRemoved = new Event<Participant[]>();
    public onError = new Event<GoloomSdkError>();
    public onConnectionStatusChanged = new Event<ConnectionStatusEvent>();
    public onSlotsChanged = new Event<SessionEvent['slotsChanged']>();
    public onLocalVad = new Event<boolean>();
    public onDisplayMediaStopped = new Event<boolean>();
    public onDisplayMediaStarted = new Event<boolean>();
    public onAudioTracksAdded = new Event<MediaStreamTrack>();
    public onAudioTracksRemoved = new Event<MediaStreamTrack>();
    public onNotification = new Event<NotificationEvent>();
    public onNoiseCancellationTypeChanged = new Event<NoiseCancellationTypeChangedEvent>();
    public onNetworkQualityChanged = new Event<NetworkQualityChangedEvent>();
    public onVirtualBackgroundTypeChanged = new Event<VirtualBackgroundTypeChangedEvent>();
    public *init() {
        const config = yield* select(selectInitSessionConfig);

        this.session = new Session({
            soundProcessing: config.soundProcessing,
            videoProcessing: config.videoProcessing,
            flags: {
                ...DEFAULT_FLAGS,
                ...config.flags,
            },
        });

        this.subscribeToEvents();
        this.onInit.dispatch(true);
    }

    public *reinit() {
        yield* call([this, this.destroy]);
        yield* call([this, this.init]);
    }

    public getMe(): Maybe<Participant> {
        if (!this.myUserId) {
            return;
        }

        return {
            userId: this.myUserId,
            name: this.name || '',
            description: '',
            sendAudio: this.session?.audioStatus,
            sendVideo: this.session?.videoStatus,
            networkScore: this.networkScore,
            connectionType: ConnectionType.CONNECTION_TYPE_SDK,
            refParticipant: null,
        };
    }

    public getLocalVad(): boolean {
        return this.localVad;
    }

    public getNetworkScore(): NetworkQualityScore {
        return this.networkScore;
    }

    public get isSessionExisting() {
        return Boolean(this.session);
    }

    public *join(config: ConnectionConfig) {
        if (!this.session) {
            return undefined;
        }

        this.roomId = config.roomId;
        this.myUserId = config.userId;
        this.name = config.name;

        const participants = yield* call([this, this.connectToRoom], {
            ...config,
            server: config.server || DEFAULT_MEDIA_SERVER_URL,
            serviceName: config.serviceName || DEFAULT_SERVICE_NAME,
            iceServers: config.iceServers || DEFAULT_ICE_SERVERS,
        });
        this.onJoin.dispatch();

        this.subscribeToConnectionEvents();

        return Array.from(participants);
    }

    public setName(name: string) {
        this.name = name;
        this.session?.setName(name);
    }

    public get participants(): Participant[] {
        if (!this.session) {
            return [];
        }

        return this.session.participants;
    }

    public startScreenSharing(constraints: DisplayConstraints | ElectronDisplayConstraints) {
        if (!this.session) {
            return Promise.resolve(undefined);
        }

        return this.session.startDisplayMedia(constraints);
    }

    public stopScreenSharing() {
        if (!this.session) {
            return Promise.resolve(undefined);
        }

        return this.session.stopDisplayMedia();
    }

    public *replaceLocalTrack(track: MediaStreamTrack, forceUnmute?: boolean) {
        const device = yield* call(createDeviceFromTrack, track);

        if (device && this.session) {
            yield* call([this.session, this.session.selectDevice], device);
            yield* call([this.session, this.session.applySelectedDevices]);

            if (forceUnmute) {
                yield* call([this.session, this.session.setAudioStatus], true);
                yield* call([this.session, this.session.setVideoStatus], true);
            }
        }
        return track;
    }

    public getRemoteAudioTracks(): MediaStreamTrack[] {
        if (!this.session) {
            return [];
        }
        return this.session.audioTracks;
    }

    public setSoundProcessorOptions(soundProcessing: SoundProcessorOptions) {
        if (!this.session) {
            return;
        }

        this.session.setSoundProcessorOptions(soundProcessing);
    }

    public setVideoProcessorOptions(videoProcessor: VideoProcessorOptions) {
        if (!this.session) {
            return;
        }

        return this.session.setVideoProcessorOptions(videoProcessor);
    }

    @bind
    private handleConnectionStatusChanged(event: SessionEvent['connectionStatusChanged']) {
        const wasStatus = this.connectionStatus;

        this.connectionStatus = event.connectionStatus;
        this.onConnectionStatusChanged.dispatch({
            current: event.connectionStatus,
            prev: wasStatus,
        });
    }

    @bind
    private handleError(event: SessionEvent['error']) {
        this.onError.dispatch({ error: event });
    }

    @bind
    handleLocalVad(event: SessionEvent['localVad']) {
        if (this.localVad === event.active) {
            return;
        }

        this.localVad = event.active;
        this.onLocalVad.dispatch(event.active);
    }

    @bind
    private handleParticipantsChanged(event: SessionEvent['participantsChanged']) {
        if (!this.session) {
            return;
        }

        if (event.updated.length) {
            this.onParticipantsUpdated.dispatch(event.updated);
        }

        if (event.added.length) {
            this.onParticipantsAdded.dispatch(event.added);
        }

        if (event.removed.length) {
            this.onParticipantsRemoved.dispatch(event.removed);
        }
    }

    @bind
    private handleSlotsChanged(event: SessionEvent['slotsChanged']) {
        if (!this.session) {
            return;
        }

        this.onSlotsChanged.dispatch(event);
    }

    @bind
    private handleDisplayMediaStarted() {
        if (!this.session) {
            return;
        }

        this.onDisplayMediaStarted.dispatch(true);
    }

    @bind
    private handleDisplayMediaStopped() {
        if (!this.session) {
            return;
        }

        this.onDisplayMediaStopped.dispatch(true);
    }

    @bind
    private handleAudioTracksChanged(event: SessionEvent['audioTracksChanged']) {
        if (!this.session) {
            return;
        }

        for (const track of event.added) {
            this.onAudioTracksAdded.dispatch(track);
        }

        for (const track of event.removed) {
            this.onAudioTracksRemoved.dispatch(track);
        }
    }

    @bind
    private handleNotification(event: SessionEvent['notification']) {
        if (!this.session) {
            return;
        }

        this.onNotification.dispatch(event);
    }

    @bind
    private handleSoundProcessorOptionsChanged(event: SessionEvent['soundProcessorOptionsChanged']) {
        if (!this.session) {
            return;
        }

        const noiseCancellationType = (() => {
            const value = event.config.noiseCancellation;

            if (!value) {
                return NoiseCancellationType.DISABLED;
            }

            if (value === true) {
                return NoiseCancellationType.AUTO;
            }

            if (typeof value === 'object' && value.type === 'rnnoise') {
                return NoiseCancellationType.LOW;
            }
        })();

        if (noiseCancellationType) {
            this.onNoiseCancellationTypeChanged.dispatch({ type: noiseCancellationType, reason: event.reason });
        }
    }
    private handleVideoProcessingOptionsChanged(event: SessionEvent['videoProcessingOptionsChanged']) {
        if (!this.session) {
            return;
        }

        const virtualBackgroundType = (() => {
            const value = event.current;

            if (!value.backgroundProcessorOptions) {
                return VirtualBackgroundType.NONE;
            }

            if (value.backgroundProcessorOptions.type === 'blur') {
                return VirtualBackgroundType.BLURRED;
            }

            return VirtualBackgroundType.NONE;
        })();

        this.onVirtualBackgroundTypeChanged.dispatch({ type: virtualBackgroundType });
    }

    @bind
    private handleSelfQualityReport(event: SessionEvent['selfQualityReport']) {
        if (!this.session) {
            return;
        }

        // Игнорируем неизвестное состояние, чтобы не врать пользователю
        if (event.networkScore === NetworkQualityScore.NETWORK_QUALITY_SCORE_UNSPECIFIED) {
            return;
        }

        this.onNetworkQualityChanged.dispatch({ value: event.networkScore, prev: this.networkScore });
        this.networkScore = event.networkScore;
    }

    private subscribeToEvents() {
        if (!this.session) {
            return;
        }

        this.session.addAsyncEventListener('participantsChanged', this.handleParticipantsChanged);
        this.session.addAsyncEventListener('localVad', this.handleLocalVad);
        this.session.addAsyncEventListener('slotsChanged', this.handleSlotsChanged);
        this.session.addAsyncEventListener('displayMediaStarted', this.handleDisplayMediaStarted);
        this.session.addAsyncEventListener('displayMediaStopped', this.handleDisplayMediaStopped);
        this.session.addAsyncEventListener('audioTracksChanged', this.handleAudioTracksChanged);
        this.session.addAsyncEventListener('notification', this.handleNotification);
        this.session.addAsyncEventListener('soundProcessorOptionsChanged', this.handleSoundProcessorOptionsChanged);
        this.session.addAsyncEventListener('selfQualityReport', this.handleSelfQualityReport);
        this.session.addAsyncEventListener('videoProcessingOptionsChanged', this.handleVideoProcessingOptionsChanged);
    }

    private subscribeToConnectionEvents() {
        if (!this.session) {
            return;
        }

        this.session.addAsyncEventListener('error', this.handleError);
        this.session.addAsyncEventListener('connectionStatusChanged', this.handleConnectionStatusChanged);
    }

    private unsubscribeFromEvents() {
        if (!this.session) {
            return;
        }

        this.session.removeAsyncEventListener('participantsChanged', this.handleParticipantsChanged);
        this.session.removeAsyncEventListener('error', this.handleError);
        this.session.removeAsyncEventListener('connectionStatusChanged', this.handleConnectionStatusChanged);
        this.session.removeAsyncEventListener('localVad', this.handleLocalVad);
        this.session.removeAsyncEventListener('slotsChanged', this.handleSlotsChanged);
        this.session.removeAsyncEventListener('displayMediaStarted', this.handleDisplayMediaStarted);
        this.session.removeAsyncEventListener('displayMediaStopped', this.handleDisplayMediaStopped);
        this.session.removeAsyncEventListener('audioTracksChanged', this.handleAudioTracksChanged);
        this.session.removeAsyncEventListener('notification', this.handleNotification);
        this.session.removeAsyncEventListener('soundProcessorOptionsChanged', this.handleSoundProcessorOptionsChanged);
        this.session.removeAsyncEventListener('selfQualityReport', this.handleSelfQualityReport);
        this.session.removeAsyncEventListener(
            'videoProcessingOptionsChanged',
            this.handleVideoProcessingOptionsChanged
        );
    }

    private async connectToRoom(config: ConnectionConfig) {
        if (!this.session) {
            return [];
        }

        const session = this.session;
        let disposeErrorListener: () => void = () => undefined;
        let disposeConnectionStatusListener: () => void = () => undefined;
        let hasPreJoinError = false;

        disposeErrorListener = session.addAsyncEventListener('error', (error) => {
            hasPreJoinError = true;

            logger.warn(error, {
                method: 'handlePreJoinSessionError',
                additional: {
                    kind: 'pre_join_session_error',
                    code: error.code,
                    reason: error.reason,
                    cause: getCauseObject(error.cause),
                    roomId: config.roomId,
                    userId: config.userId,
                },
            });
        });

        disposeConnectionStatusListener = session.addAsyncEventListener('connectionStatusChanged', (event) => {
            if (event.connectionStatus !== ConnectionStatus.FAILED || hasPreJoinError) {
                return;
            }

            logger.warn(new Error('Session status changed to failed before connectToRoom resolved'), {
                method: 'handlePreJoinFailedConnectionStatus',
                additional: {
                    kind: 'pre_join_connection_failed',
                    errorCode: session.errorCode,
                    networkConfig: session.networkConfiguration,
                    roomId: config.roomId,
                    userId: config.userId,
                    status: event.connectionStatus,
                },
            });
        });

        try {
            return await session.connectToRoom(config);
        } finally {
            disposeErrorListener();
            disposeConnectionStatusListener();
        }
    }

    public async destroy() {
        this.onDestroy.dispatch(true);
        this.unsubscribeFromEvents();
        await this.session?.destroy();
        this.session = undefined;
    }
}
